import Grid from "@mui/material/Grid";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { useStore } from "utils/useStore";
import { EvalReviewStore } from "./Stores/EvalReviewStore";
import AcxButton from "components/UI/AcxButton";
import { LayoutDrawerStore } from "stores/Layout/LayoutDrawerStore";
import { Stack, Typography } from "@mui/material";
import ConversationsStore, {
    SidePanelType,
} from "components/Conversations/Stores/ConversationsStore";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import ConvoEvalReviewDetailsForm from "./Views/ConvoEvalReviewDetailsForm";
import FlagCircleIcon from "@mui/icons-material/FlagCircle";
import {
    AgentCoaching,
    AgentCoachingStatus,
} from "models/AgentModels/AgentCoaching";
import moment from "moment";

type Props = { drawerStore: LayoutDrawerStore };

const ConvoEvalReview: React.FC<Props> = observer((props: Props) => {
    const store = useStore(EvalReviewStore);
    const conversationsStore = useStore(ConversationsStore);

    const actionButtons = (
        <Stack
            direction={"row"}
            width={"100%"}
            justifyContent={"flex-end"}
            gap={1}
        >
            {!store.isCoached && (
                <>
                    <Grid item>
                        <AcxButton
                            tooltip="Save progress on this review"
                            onClick={onSaveAsDraft}
                            color="white"
                            loading={
                                conversationsStore.anyTaskLoading ||
                                store.isGeneratingCoachingNotesFromKBo
                            }
                        >
                            Save as Draft
                        </AcxButton>
                    </Grid>

                    <Grid item>
                        <AcxButton
                            tooltip={
                                store.isCoached
                                    ? "Submit acknowledgement"
                                    : "Submit review, cannot be edited"
                            }
                            onClick={onSaveAsComplete}
                            loading={
                                conversationsStore.anyTaskLoading ||
                                store.isGeneratingCoachingNotesFromKBo
                            }
                            id="complete-coaching"
                        >
                            Complete
                        </AcxButton>
                    </Grid>
                </>
            )}
        </Stack>
    );

    useEffect(() => {
        props.drawerStore?.setFooterButtons(actionButtons);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionButtons, props.drawerStore?.isOpen, store.isCoached]);

    const goBack = () => {
        conversationsStore.setSidePanel(SidePanelType.Hidden);
        props.drawerStore.closeAndResetDrawer();
    };

    async function onSaveAsComplete() {
        let agentCoachingResponse: AgentCoaching | undefined;
        if (!store.agentCoaching) {
            console.warn("Unable to update agent coaching");
            return;
        }
        if (store.isEditing)
            agentCoachingResponse = await store.updateAgentCoachingNew(true);
        else agentCoachingResponse = await store.createAgentCoachingNew(false);

        if (!!agentCoachingResponse) {
            if (store.agentCoaching) {
                store.agentCoaching.coachId = agentCoachingResponse.coachId;
                store.agentCoaching.createdBy = agentCoachingResponse.createdBy;

                conversationsStore.agentCoaching = [agentCoachingResponse];
            }
            store.isEditing = false;
            goBack();
        }
    }

    async function onSaveAsDraft() {
        if (!store.agentCoaching) {
            console.warn("Unable to update agent coaching");
            return;
        }
        let agentCoachingResponse: AgentCoaching | undefined;
        if (store.isEditing)
            agentCoachingResponse = await store.updateAgentCoachingNew(false);
        else agentCoachingResponse = await store.createAgentCoachingNew(true);

        if (agentCoachingResponse) {
            if (store.agentCoaching) {
                store.agentCoaching.coachId = agentCoachingResponse.coachId;
                store.agentCoaching.createdBy = agentCoachingResponse.createdBy;

                conversationsStore.agentCoaching = [agentCoachingResponse];
            }
            conversationsStore.agentCoaching = [agentCoachingResponse];

            // Keep the drawer open so the coach can continue editing and save
            // the draft as many times as they need (AUT-10104). The draft is now
            // tracked as editing, so further saves update it in place.
        }
    }

    if (
        conversationsStore.anyTaskLoading ||
        store.isGeneratingCoachingNotesFromKBo
    ) {
        return <AcxLoadingIndicator size={24} alternate="PuffLoader" />;
    }

    const coaching = store.agentCoaching;
    const currentUser = conversationsStore.authStore.permStore.user;
    const coachedByName =
        [coaching?.coach?.firstName, coaching?.coach?.lastName]
            .filter(Boolean)
            .join(" ") ||
        (coaching?.coachId === currentUser?.id
            ? [currentUser?.firstName, currentUser?.lastName]
                  .filter(Boolean)
                  .join(" ")
            : "") ||
        coaching?.createdBy ||
        "Unknown user";

    const formattedDraftModifiedDate = moment
        .utc(store.draftSavedOn ?? store.agentCoaching?.modifiedOn ?? "")
        .local()
        .format("MM/DD/YYYY, h:mm a");

    return (
        <Grid container direction="column" flexWrap="nowrap" height={"100%"}>
            {store.isEditing &&
            store.agentCoaching?.coachingStatus ===
                AgentCoachingStatus.InProgress ? (
                <Stack direction={"row"} p={2} pb={0} alignItems={"center"}>
                    <FlagCircleIcon color="disabled" />
                    <Typography
                        sx={{ textWrap: "auto" }}
                        pl={1}
                        fontSize={14}
                        fontWeight={400}
                    >
                        Draft saved on {formattedDraftModifiedDate}
                    </Typography>
                </Stack>
            ) : (
                store.isCoached && (
                    <Stack direction={"row"} p={2} pb={0} alignItems={"center"}>
                        <FlagCircleIcon color="secondary" />
                        <Typography
                            sx={{ textWrap: "auto" }}
                            pl={1}
                            fontSize={14}
                            fontWeight={400}
                        >
                            Coached by {coachedByName} on{" "}
                            {formattedDraftModifiedDate}
                        </Typography>
                    </Stack>
                )
            )}
            <ConvoEvalReviewDetailsForm isCoached={store.isCoached} />
        </Grid>
    );
});

export default ConvoEvalReview;
